import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import AlzijLogo from './AlzijLogo';
import LanguageSwitcher from './flags/LanguageSwitcher';

const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  
  const [hoveredLink, setHoveredLink] = useState(null);
  const [hoveredButton, setHoveredButton] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [reachedServicesSection, setReachedServicesSection] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Scroll & resize listeners - only track scroll for Home page
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    
    // Function to detect when services section is visible - simpler approach
    const handleScroll = () => {
      // Basic scroll detection for isScrolled state
      setIsScrolled(window.scrollY > 100);
      
      // Simpler detection for Services section - based on viewport height
      // Since we know Services comes after the first full-screen section
      const viewportHeight = window.innerHeight;
      
      // Check if we've scrolled at least 80% of the first viewport
      // This works because the first section in Home.jsx is min-h-screen
      if (window.scrollY >= viewportHeight) {
        setReachedServicesSection(true);
      } else {
        setReachedServicesSection(false);
      }
    };

    window.addEventListener('resize', handleResize);
    
    // Only attach scroll listener on Home page
    if (isHomePage) {
      window.addEventListener('scroll', handleScroll);
      // Run immediately to set initial state
      handleScroll();
    } else {
      // For non-Home pages, force the scrolled state
      setIsScrolled(true);
      setReachedServicesSection(true);
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (isHomePage) {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isHomePage]); // Re-run when page changes

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (mobileNavOpen) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      
      const preventTouchMove = (e) => {
        if (mobileNavOpen) {
          e.preventDefault();
        }
      };
      
      document.addEventListener('touchmove', preventTouchMove, { passive: false });
      
      return () => {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        document.removeEventListener('touchmove', preventTouchMove);
      };
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
  }, [mobileNavOpen]);

  const toggleMobileMenu = () => {
    setMobileNavOpen(o => !o);
  };

  // Modified logo styles to consider the current route
  const getLogoStyles = () => {
    // Base styles with consistent transitions for all properties
    const base = { 
      position: 'absolute', 
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
      willChange: 'transform, top, left' 
    };
    
    // For non-homepage routes, always use the shrunk state regardless of scroll
    const shouldUseShrunkStyle = !isHomePage || isScrolled || mobileNavOpen;
    
    // Pre-calculate final positions to avoid in-between calculations during transitions
    if (windowWidth < 640) {
      return {
        ...base,
        ...(shouldUseShrunkStyle
          ? { top: '3.85rem', left: '-4.9rem', transform: 'translateY(-50%) scale(0.6)' }
          : { top: '-3rem', left: '-4.7rem', transform: 'scale(0.5)' }),
      };
    } else if (windowWidth < 768) {
      return {
        ...base,
        ...(shouldUseShrunkStyle
          ? { top: '50%', left: '24px', transform: 'translateY(-50%) scale(0.8)' }
          : { top: '5px', left: '24px', transform: 'scale(0.95)' }),
      };
    } else if (windowWidth < 1024) {
      return {
        ...base,
        ...(shouldUseShrunkStyle
          ? { top: '50%', left: '32px', transform: 'translateY(-50%) scale(0.8)' }
          : { top: '0px', left: '32px', transform: 'scale(1)' }),
      };
    } else {
      return {
        ...base,
        ...(shouldUseShrunkStyle
          ? { top: '4.5rem', left: '2rem', transform: 'translateY(-50%) scale(0.85)' }
          : { top: '-5px', left: '40px', transform: 'scale(1)' }),
      };
    }
  };

  // Updated navLinks with 'to' prop for React Router
  const navLinks = [
    { label: t('home'), to: '/' },
    { label: t('about'), to: '/over-ons' },
    { label: t('services'), to: '/diensten' },
    { label: t('contact'), to: '/contact' },
  ];

  // Determine if header should appear in "scrolled" state (always true for non-Home pages)
  const shouldUseScrolledStyle = !isHomePage || isScrolled || reachedServicesSection;

  // Header background styles - modified to always use scrolled style on non-Home pages
  const headerBg = mobileNavOpen
    ? 'header-paper-bg'
    : !isHomePage 
    ? 'backdrop-blur-md shadow-md header-paper-bg' // Always use scrolled style for non-Home pages
    : reachedServicesSection
    ? 'backdrop-blur-sm shadow-sm header-paper-bg' // White with slight transparency when Services is visible
    : isScrolled
    ? 'backdrop-blur-md shadow-md header-paper-bg' // More blurry when just scrolled
    : windowWidth < 768
    ? 'header-paper-bg'
    : 'bg-transparent';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${headerBg} h-[4rem]`}
    >
      <div className="h-[4rem] flex items-center justify-between px-6 relative">
        {/* Logo with conditional forceAnimate based on route */}
        <div style={getLogoStyles()}>
  {/* Wrapper with pointer-events: none */}
  <div style={{ pointerEvents: 'none' }}>
    <NavLink to="/" style={{ pointerEvents: 'none' }}>
      {/* This div creates the properly sized clickable area */}
      <div 
        className="absolute" 
        style={{
          width: 260 * (isHomePage ? (isScrolled || mobileNavOpen ? 0.5 : 0.7) : 0.5),
          height: 350 * (isHomePage ? (isScrolled || mobileNavOpen ? 0.5 : 0.7) : 0.5),
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'auto',
          cursor: 'pointer',
          zIndex: 10
        }}
      />
      <AlzijLogo 
        forceAnimate={isHomePage ? (isScrolled || mobileNavOpen) : true} 
        isHomePage={isHomePage} 
      />
    </NavLink>
  </div>
</div>

        {/* Desktop nav */}
        <nav className="hidden md:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <ul
            className="flex space-x-8 items-center uppercase"
            style={{ fontFamily: "'Space Mono', monospace" }}
          >
            {navLinks.map((link, idx) => (
              <li
                key={idx}
                onMouseEnter={() => setHoveredLink(idx)}
                onMouseLeave={() => setHoveredLink(null)}
                className={`text-base transition-all duration-300 transform ${
                  hoveredLink !== null && hoveredLink !== idx
                    ? 'scale-90 text-gray-600 translate-y-2'
                    : `scale-100 translate-y-0 ${
                        shouldUseScrolledStyle ? 'text-black' : 'text-white'
                      }`
                }`}
              >
                <NavLink 
                  to={link.to} 
                  className={({ isActive }) => `flex items-center ${isActive ? 'font-bold' : ''}`}
                  onClick={() => {
                    if (mobileNavOpen) {
                      toggleMobileMenu();
                    }
                  }}
                >
                  {link.label}
                  <span className={`inline-block duration-300 w-1.5 h-1.5 ml-1 ${
                    hoveredLink !== null && hoveredLink !== idx
                      ? 'bg-gray-600' 
                      : shouldUseScrolledStyle
                        ? 'bg-emerald-800' 
                        : 'bg-white'
                  }`}></span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Right side container for language switcher and CTA */}
        <div className="hidden md:flex items-center space-x-4 ml-auto">
          {/* Desktop CTA */}
          <NavLink
            to="/contact"
            className="relative overflow-hidden transition-all duration-300"
            onMouseEnter={() => setHoveredButton(true)}
            onMouseLeave={() => setHoveredButton(false)}
            style={{ fontFamily: "'Space Mono', monospace" }}
          >
            <span className="relative z-10 text-white text-sm px-4 py-1.5 inline-block">
              {t('lets_call')}
            </span>
            <span
              className="absolute inset-0 bg-green-900 hover:bg-green-700 transition-all duration-300"
              style={{
                transform: hoveredButton ? 'scale(0.9)' : 'scale(1)',
                transition: 'transform 0.3s ease',
              }}
            />
          </NavLink>
          
          {/* Language Switcher - Desktop only */}
          <div className="z-50">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Custom Hamburger Menu - Mobile only */}
        <div className="md:hidden ml-auto flex items-center h-full">
          <button
            onClick={toggleMobileMenu}
            aria-label={mobileNavOpen ? 'Close Menu' : 'Open Menu'}
            className="flex items-center justify-center h-12 w-12 focus:outline-none relative"
            style={{ isolation: 'isolate' }}
          >
            <div className="relative w-7 h-7">
              {/* Main rotation container */}
              <div 
                className="absolute inset-0 transition-transform duration-300 ease-in-out"
                style={{
                  transform: mobileNavOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                  transformOrigin: 'center',
                  willChange: 'transform',
                }}
              >
                {/* Top-right line */}
                <div 
                  className="absolute h-0.5 w-3.5 bg-black origin-center transition-all duration-300 ease-in-out"
                  style={{ 
                    top: '0',
                    right: '0',
                    transform: mobileNavOpen 
                      ? 'translate(-50%, 50%) rotate(90deg)' 
                      : 'translate(0, 0) rotate(0deg)',
                    willChange: 'transform',
                  }}
                />
                
                {/* Top-right vertical line */}
                <div 
                  className="absolute w-0.5 h-3.5 bg-black origin-center transition-all duration-300 ease-in-out"
                  style={{ 
                    top: '0',
                    right: '0',
                    transform: mobileNavOpen 
                      ? 'translate(-50%, 50%) rotate(270deg)' 
                      : 'translate(0, 0) rotate(0deg)',
                    willChange: 'transform',
                  }}
                />
                
                {/* Bottom-left horizontal line */}
                <div 
                  className="absolute h-0.5 w-3.5 bg-black origin-center transition-all duration-300 ease-in-out"
                  style={{ 
                    bottom: '0',
                    left: '0',
                    transform: mobileNavOpen 
                      ? 'translate(50%, -50%) rotate(90deg)' 
                      : 'translate(0, 0) rotate(0deg)',
                    willChange: 'transform',
                  }}
                />
                
                {/* Bottom-left vertical line */}
                <div 
                  className="absolute w-0.5 h-3.5 bg-black origin-center transition-all duration-300 ease-in-out"
                  style={{ 
                    bottom: '0',
                    left: '0',
                    transform: mobileNavOpen 
                      ? 'translate(50%, -50%) rotate(270deg)'
                      : 'translate(0, 0) rotate(0deg)',
                    willChange: 'transform',
                  }}
                />
              </div>
              
              {/* Top hamburger line */}
              <div 
                className="absolute h-0.5 bg-black transition-all duration-300 ease-in-out" 
                style={{ 
                  top: 'calc(50% - 4px)',
                  width: mobileNavOpen ? '0' : '1rem',
                  left: mobileNavOpen ? '50%' : '0.375rem',
                  opacity: mobileNavOpen ? '0' : '1',
                  transform: mobileNavOpen ? 'translateX(-50%)' : 'translateX(0)',
                  willChange: 'width, opacity, transform',
                  pointerEvents: 'none',
                }}
              />
              
              {/* Middle hamburger line */}
              <div 
                className="absolute h-0.5 bg-black transition-all duration-300 ease-in-out"
                style={{ 
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: mobileNavOpen ? '0' : '1rem',
                  left: mobileNavOpen ? '50%' : '0.375rem',
                  opacity: mobileNavOpen ? '0' : '1',
                  willChange: 'width, opacity, transform',
                  pointerEvents: 'none',
                }}
              />
              
              {/* Bottom hamburger line */}
              <div 
                className="absolute h-0.5 bg-black transition-all duration-300 ease-in-out"
                style={{ 
                  bottom: 'calc(50% - 4px)',
                  width: mobileNavOpen ? '0' : '1rem',
                  left: mobileNavOpen ? '50%' : '0.375rem',
                  opacity: mobileNavOpen ? '0' : '1',
                  transform: mobileNavOpen ? 'translateX(-50%)' : 'translateX(0)',
                  willChange: 'width, opacity, transform',
                  pointerEvents: 'none',
                }}
              />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile menu - Now positioned absolutely outside the header flow */}
      <div
        className={`md:hidden header-paper-bg absolute top-[4rem] inset-x-0 transition-all duration-500 ease-in-out ${
          mobileNavOpen
            ? 'opacity-100 h-[calc(100vh-4rem)] z-30'
            : 'opacity-0 h-0 -z-10 pointer-events-none'
        }`}
      >
        <nav className="px-8 py-8">
          <ul
            className="flex flex-col space-y-8 items-start uppercase"
            style={{ fontFamily: "'Space Mono', monospace" }}
          >
            {navLinks.map((link, i) => (
              <li
                key={i}
                className="w-full text-xl font-bold text-black border-b border-gray-200 pb-2"
              >
                <NavLink 
                  to={link.to}
                  className={({isActive}) => `block w-full flex items-center ${isActive ? 'text-emerald-800' : ''}`}
                  onClick={toggleMobileMenu}
                >
                  {link.label}
                  <span className="inline-block w-1.5 h-1.5 bg-emerald-800 ml-1"></span>
                </NavLink>
              </li>
            ))}
            <li className="pt-6">
              <NavLink
                to="/contact"
                className="relative overflow-hidden transition-all duration-300 w-full text-left"
                style={{ fontFamily: "'Space Mono', monospace" }}
                onClick={toggleMobileMenu}
              >
                <span className="relative z-10 text-white text-base px-6 py-3 inline-block">
                  {t('lets_call')}
                </span>
                <span className="absolute inset-0 bg-green-900 transition-all duration-300" />
              </NavLink>
            </li>
            
            {/* Add language switcher to mobile menu (only visible when menu is open) */}
            <li className="pt-6 pl-2">
              <LanguageSwitcher />
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;
